import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { 
  Search, 
  Filter, 
  Folder,
  Plus,
  LayoutGrid,
  List,
  ArrowLeft,
  FileText
} from 'lucide-react';
import { Button } from '@/components/ui/Button';
import axios from 'axios';
import toast from 'react-hot-toast';
import CreateFileWizard from '@/components/efile/CreateFileWizard';
import CreateFolderModal from '@/components/efile/CreateFolderModal';
import RenameFileModal from '@/components/efile/RenameFileModal';
import FileListView from '@/components/efile/FileListView';
import ConfirmDialog from '@/components/ui/ConfirmDialog';
import Modal from '@/components/ui/Modal';
import SearchableCombobox from '@/components/ui/SearchableCombobox';

const FileBrowser = ({ 
  apiEndpoint, 
  pageTitle, 
  pageDescription, 
  userColumnLabel, 
  defaultViewMode = 'grid',
  serverSideFiltering = false,
  allowFileCreation = true,
  allowFolderCreation = true,
  enableMoveToFolder = false,
  showDeskSwitch = false
}) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState(defaultViewMode);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isCreateFolderModalOpen, setIsCreateFolderModalOpen] = useState(false);
  const [currentFolderId, setCurrentFolderId] = useState(null);
  const [currentFolder, setCurrentFolder] = useState(null);
  const [breadcrumbFolders, setBreadcrumbFolders] = useState([]);
  
  // Rename state
  const [fileToRename, setFileToRename] = useState(null);
  const [isRenameModalOpen, setIsRenameModalOpen] = useState(false);

  // Delete state
  const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, file: null });

  const [moveModal, setMoveModal] = useState({ isOpen: false, file: null, folderId: '' });

  // Determine permissions based on current folder
  const canCreateFile = typeof allowFileCreation === 'function' 
    ? allowFileCreation(currentFolderId) 
    : allowFileCreation;
    
  const canCreateFolder = typeof allowFolderCreation === 'function' 
    ? allowFolderCreation(currentFolderId) 
    : allowFolderCreation;

  useEffect(() => {
    fetchFiles();
  }, [currentFolderId, apiEndpoint]);

  // Fetch current folder details for title/breadcrumbs
  useEffect(() => {
    if (currentFolderId) {
       const fetchInfo = async () => {
           try {
               const token = localStorage.getItem('token');
               const response = await axios.get(`/api/efile/folder/${currentFolderId}`, {
                  headers: { Authorization: `Bearer ${token}` }
               });
               if (response.data.success) {
                  setCurrentFolder(response.data.data);
               }
           } catch (e) {
               console.error(e);
               // If we can't fetch folder info, maybe it's because we don't have access or it's deleted.
               // For My Desk, if we are in a folder, we should have access.
           }
       };
       fetchInfo();
    } else {
       setCurrentFolder(null);
    }
  }, [currentFolderId]);

  useEffect(() => {
    const buildBreadcrumbs = async () => {
      if (!currentFolderId) {
        setBreadcrumbFolders([]);
        return;
      }

      const token = localStorage.getItem('token');
      const chain = [];
      const visited = new Set();
      let cursorId = currentFolderId;

      for (let i = 0; i < 20 && cursorId && !visited.has(String(cursorId)); i++) {
        visited.add(String(cursorId));
        let folder = null;

        if (currentFolder && String(currentFolder.id) === String(cursorId)) {
          folder = currentFolder;
        } else {
          try {
            const response = await axios.get(`/api/efile/folder/${cursorId}`, {
              headers: { Authorization: `Bearer ${token}` }
            });
            if (response.data.success) folder = response.data.data;
          } catch {
            folder = null;
          }
        }

        if (!folder) break;
        chain.unshift(folder);
        cursorId = folder.parentId;
      }

      setBreadcrumbFolders(chain);
    };

    buildBreadcrumbs();
  }, [currentFolderId, currentFolder]);

  const fetchFiles = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Authentication required');
        navigate('/login');
        return;
      }

      const params = { limit: 100 };
      if (serverSideFiltering) {
        params.parentId = currentFolderId || 'null';
      } else {
        if (String(apiEndpoint || '').includes('/api/efile/active')) {
          params.all = 1;
          params.limit = 5000;
        } else {
          params.limit = 2000;
        }
      }

      const response = await axios.get(apiEndpoint, {
        headers: { Authorization: `Bearer ${token}` },
        params
      });

      if (response.data.success) {
        const responseData = response.data.data;
        // Handle both array and paginated object responses
        const filesData = Array.isArray(responseData) 
            ? responseData 
            : (responseData.data || responseData.files || []);
            
        setFiles(filesData);
      }
    } catch (error) {
      console.error('Error fetching files:', error);
      toast.error(error?.response?.data?.message || error?.message || 'Failed to load files');
    } finally {
      setLoading(false);
    }
  };

  const handleFileCreated = (newFile) => {
    console.log('File Created:', newFile, 'Current Folder:', currentFolderId);
    
    // Enrich new file with current user details for better optimistic UI
    const enrichedFile = {
        ...newFile,
        createdByName: user?.name,
        currentDeskName: user?.name,
        currentDeskDesignation: user?.designation,
        notingCount: 0
    };
    
    // Immediate state update for responsiveness
    setFiles(prev => {
        // Avoid duplicates
        if (prev.find(f => f.id === newFile.id)) return prev;
        return [enrichedFile, ...prev];
    });

    // Refresh from server to ensure data consistency
    // Small delay to ensure DB commit is visible to subsequent read
    setTimeout(() => {
        fetchFiles();
    }, 100);
  };

  const handleFolderCreated = (newFolder) => {
     if (serverSideFiltering) {
        const folderParentId = newFolder.parentId || null;
        const currentId = currentFolderId || null;
        
        if (folderParentId == currentId) {
            setFiles(prev => [newFolder, ...prev]);
        }
     } else {
        setFiles(prev => [newFolder, ...prev]);
    }
    // Refresh from server to ensure data consistency
    fetchFiles();
  };

  const handleFileClick = (file) => {
    if (file.isFolder) {
      setCurrentFolderId(file.id);
      setSearchTerm('');
    } else {
      navigate(`/efile/${file.id}`);
    }
  };

  const handleBack = () => {
    if (currentFolderId) {
        if (currentFolder && currentFolder.parentId) {
            setCurrentFolderId(currentFolder.parentId);
        } else {
            // If currentFolder is null (not fetched yet?) or parentId is null
            // For client side filtering, we might find it in 'files' if we have all files
            if (!serverSideFiltering) {
                 const folder = files.find(f => f.id === currentFolderId);
                 if (folder) {
                     setCurrentFolderId(folder.parentId);
                     return;
                 }
            }
            // Default to root
            setCurrentFolderId(null);
        }
    }
  };

  const getFilteredFiles = () => {
      let filtered = files || [];

      // Client-side filtering for hierarchy
      if (!serverSideFiltering) {
          if (currentFolderId) {
              filtered = files.filter(f => f.parentId === currentFolderId);
          } else {
              // Show root files AND orphaned files (files whose parent is not in the list)
              const allIds = new Set(files.map(f => f.id));
              filtered = files.filter(f => !f.parentId || !allIds.has(f.parentId));
          }
      }

      // Search filtering
      if (searchTerm) {
          // Global search in My Desk (client-side)
          if (!serverSideFiltering) {
              filtered = files.filter(file => 
                  file.subject?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                  file.fileNumber?.toLowerCase().includes(searchTerm.toLowerCase())
              );
          } else {
              // Local search in File Workspace (current folder only)
              filtered = filtered.filter(file => 
                file.subject?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                file.fileNumber?.toLowerCase().includes(searchTerm.toLowerCase())
            );
          }
      }

      return filtered.sort((a, b) => {
        if (a.isFolder && !b.isFolder) return -1;
        if (!a.isFolder && b.isFolder) return 1;
        return new Date(b.updatedAt) - new Date(a.updatedAt);
      });
  };

  const filteredFiles = getFilteredFiles();

  const handleRename = (file) => {
    setFileToRename(file);
    setIsRenameModalOpen(true);
  };
  
  const handleFileRenamed = (updatedFile) => {
    setFiles(files.map(f => f.id === updatedFile.id ? updatedFile : f));
  };

  const handleDelete = (file) => {
    setDeleteConfirm({ isOpen: true, file });
  };

  const openMoveModal = (file) => {
    const current = file?.parentId === null || file?.parentId === undefined ? '' : String(file.parentId);
    setMoveModal({ isOpen: true, file, folderId: current });
  };

  const confirmMove = async () => {
    const file = moveModal.file;
    if (!file) return;
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const folderIdToSend = moveModal.folderId === '' ? null : Number(moveModal.folderId);
      await axios.put(
        `/api/efile/${file.id}/folder`,
        { folderId: folderIdToSend },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Moved successfully');
      setFiles(prev => prev.map(f => (f.id === file.id ? { ...f, parentId: folderIdToSend } : f)));
      setMoveModal({ isOpen: false, file: null, folderId: '' });
      fetchFiles();
    } catch (error) {
      console.error('Error moving file:', error);
      toast.error(error.response?.data?.message || 'Failed to move');
    } finally {
      setLoading(false);
    }
  };

  const confirmDelete = async () => {
    const file = deleteConfirm.file;
    if (!file) return;

    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const url = file.isFolder ? `/api/efile/folder/${file.id}` : `/api/efile/${file.id}`;
      await axios.delete(url, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Deleted successfully');
      setFiles(files.filter(f => f.id !== file.id));
      fetchFiles();
    } catch (error) {
      console.error('Error deleting file:', error);
      toast.error(error.response?.data?.message || 'Failed to delete');
    } finally {
      setLoading(false);
      setDeleteConfirm({ isOpen: false, file: null });
    }
  };

  return (
    <div className="flex flex-col h-full bg-gray-50/50">
      {/* Header */}
      <div className="px-8 py-6 bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center gap-4">
            {currentFolderId && (
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={handleBack}
                className="mr-2"
              >
                <ArrowLeft className="h-4 w-4 mr-2" /> Back
              </Button>
            )}
            <div>
              <h1 className="text-2xl font-bold text-gray-900 tracking-tight">
                {currentFolder ? (currentFolder.subject || currentFolder.name) : pageTitle}
              </h1>
              <p className="text-sm text-gray-500 mt-1">{pageDescription}</p>
              {showDeskSwitch && (
                <div className="inline-flex items-center gap-1 mt-3 bg-gray-100 rounded-lg p-1">
                  <Button
                    size="sm"
                    onClick={() => navigate('/kanban')}
                    className={String(apiEndpoint).includes('/my-desk') ? 'bg-white text-indigo-700 shadow-sm' : 'bg-transparent text-gray-600 hover:bg-white'}
                  >
                    My Desk
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => navigate('/files')}
                    className={String(apiEndpoint).includes('/active') ? 'bg-white text-indigo-700 shadow-sm' : 'bg-transparent text-gray-600 hover:bg-white'}
                  >
                    Workspace
                  </Button>
                </div>
              )}
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mt-2 text-xs text-gray-500">
                <button
                  type="button"
                  onClick={() => setCurrentFolderId(null)}
                  className="hover:text-gray-700"
                >
                  {pageTitle}
                </button>
                {breadcrumbFolders.map((folder) => (
                  <React.Fragment key={folder.id}>
                    <span className="text-gray-300">/</span>
                    <button
                      type="button"
                      onClick={() => setCurrentFolderId(folder.id)}
                      className="hover:text-gray-700 max-w-[240px] truncate"
                      title={folder.subject || folder.name || ''}
                    >
                      {folder.subject || folder.name}
                    </button>
                  </React.Fragment>
                ))}
              </div>
            </div>
          </div>
          <div className="flex gap-2">
            {canCreateFolder && (
              <Button 
                variant="outline"
                onClick={() => setIsCreateFolderModalOpen(true)}
                className="bg-white hover:bg-gray-50 text-gray-700 shadow-sm"
              >
                <Folder className="mr-2 h-4 w-4" /> New Folder
              </Button>
            )}
            {canCreateFile && (
              <Button 
                onClick={() => setIsCreateModalOpen(true)}
                className="bg-indigo-600 hover:bg-indigo-700 text-white shadow-sm"
              >
                <FileText className="mr-2 h-4 w-4" /> New File
              </Button>
            )}
          </div>
        </div>

        {/* Search and Filter Bar */}
        <div className="flex items-center gap-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search files..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
            />
          </div>
          <div className="flex items-center gap-2 border-l border-gray-200 pl-4">
             <Button 
               variant="ghost" 
               size="icon"
               onClick={() => setViewMode('grid')}
               className={viewMode === 'grid' ? 'bg-gray-100 text-indigo-600' : 'text-gray-400'}
             >
               <LayoutGrid className="h-4 w-4" />
             </Button>
             <Button 
               variant="ghost" 
               size="icon"
               onClick={() => setViewMode('list')}
               className={viewMode === 'list' ? 'bg-gray-100 text-indigo-600' : 'text-gray-400'}
             >
               <List className="h-4 w-4" />
             </Button>
          </div>
          <Button variant="outline" className="text-gray-600 border-gray-200 ml-auto">
            <Filter className="mr-2 h-4 w-4" /> Filter
          </Button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto p-8">
        <FileListView 
          files={filteredFiles} 
          viewMode={viewMode}
          onFileClick={handleFileClick}
          onFileRename={handleRename}
          onFileMove={enableMoveToFolder ? openMoveModal : undefined}
          onFileDelete={handleDelete}
          isLoading={loading}
          userColumnLabel={userColumnLabel}
        />
      </div>

      <CreateFileWizard
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onFileCreated={handleFileCreated}
        parentId={currentFolderId}
      />

      <CreateFolderModal
        isOpen={isCreateFolderModalOpen}
        onClose={() => setIsCreateFolderModalOpen(false)}
        onFolderCreated={handleFolderCreated}
        parentId={currentFolderId}
        scope={String(apiEndpoint || '').includes('/my-desk') ? 'personal' : 'workspace'}
        defaultDepartmentId={String(apiEndpoint || '').includes('/my-desk') ? null : (user?.departmentId || null)}
      />
      
      <RenameFileModal
        isOpen={isRenameModalOpen}
        onClose={() => setIsRenameModalOpen(false)}
        onFileRenamed={handleFileRenamed}
        file={fileToRename}
      />

      <ConfirmDialog
        isOpen={deleteConfirm.isOpen}
        onClose={() => setDeleteConfirm({ isOpen: false, file: null })}
        onConfirm={confirmDelete}
        title="Delete Item"
        message={`Are you sure you want to delete "${deleteConfirm.file?.subject || deleteConfirm.file?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        variant="danger"
      />

      <Modal
        isOpen={moveModal.isOpen}
        onClose={() => setMoveModal({ isOpen: false, file: null, folderId: '' })}
        title="Move File"
        size="md"
      >
        <div className="space-y-4">
          <div className="text-sm text-gray-600">
            {moveModal.file?.fileNumber} {moveModal.file?.subject ? `• ${moveModal.file.subject}` : ''}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Destination Folder</label>
            <SearchableCombobox
              items={[
                { label: 'Root', value: '' },
                ...files
                  .filter(f => Boolean(f?.isFolder))
                  .map(f => ({ label: f.name, value: String(f.id) }))
              ]}
              value={moveModal.folderId}
              onChange={(val) => setMoveModal(prev => ({ ...prev, folderId: val }))}
              placeholder="Select folder"
            />
          </div>
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => setMoveModal({ isOpen: false, file: null, folderId: '' })} disabled={loading}>
              Cancel
            </Button>
            <Button onClick={confirmMove} disabled={loading}>
              Move
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default FileBrowser;
